import type { ReactNode } from 'react'
import { useTranslation } from 'react-i18next'

import { AutoSlide } from '../../components/AutoSlide'
import { peakAndTrough } from '../../components/chartGeometry'
import {
  AreaChart,
  DonutChart,
  DotPlot,
  TreemapChart,
  WaffleChart,
} from '../../components/charts'
import type { SlideWeight } from '../../lib/autoSlide'
import type { Company } from '../../lib/dashboard'
import { CHART_WEIGHT, chartGroupOf, type ChartKey, type OrgChartKey } from './statsLayout'

/** Locale formatters shared by the org chart stats and the dashboard, so one figure reads the same on both. */
export function useStatFormat() {
  const { i18n } = useTranslation()
  return {
    count: (n: number) => n.toLocaleString(i18n.language),
    hours: (n: number) => n.toLocaleString(i18n.language, { maximumFractionDigits: 1 }),
    dayLabel: (iso: string) =>
      new Date(`${iso}T00:00:00`).toLocaleDateString(i18n.language, { month: 'short', day: 'numeric' }),
  }
}

export function chartSlide(key: string, node: ReactNode, weight: SlideWeight, group?: string) {
  return { key, weight, group, node: <div className="flex w-full min-w-0 [&>figure]:w-full">{node}</div> }
}

/** The server folds a blank team or office into "—"; the reader gets a word for it instead. */
function relabelBlank<T extends { label: string }>(buckets: T[], blank: string): T[] {
  return buckets.map((b) => (b.label === '—' ? { ...b, label: blank } : b))
}

export function useCompanyCharts(data: Company | undefined): Record<ChartKey, ReactNode> {
  const { t } = useTranslation()
  const { count, hours, dayLabel } = useStatFormat()
  const away = data?.away ?? []
  const weekdays = data?.desks.by_weekday ?? []
  const busy = peakAndTrough(weekdays.map((b) => b.value))
  return {
    byTeam: (
      <DonutChart
        title={t('dashboard.byTeam')}
        buckets={relabelBlank(data?.headcount.by_team ?? [], t('homewall.noTeam'))}
        format={count}
        centerLabel={t('homewall.peopleShort')}
        emptyLabel={t('dashboard.noData')}
        animate
        compact
      />
    ),
    byWorkType: (
      <WaffleChart
        title={t('dashboard.byWorkType')}
        buckets={data?.headcount.by_work_type ?? []}
        format={count}
        unitLabel={t('homewall.peopleUnit')}
        emptyLabel={t('dashboard.noData')}
        animate
      />
    ),
    byOffice: (
      <DonutChart
        title={t('homewall.byOffice')}
        buckets={relabelBlank(data?.headcount.by_office ?? [], t('homewall.noOffice'))}
        format={count}
        centerLabel={t('homewall.peopleShort')}
        emptyLabel={t('dashboard.noData')}
        animate
        compact
      />
    ),
    away: (
      <AreaChart
        title={t('homewall.awayNext14')}
        points={away.map((d) => ({ label: dayLabel(d.day), value: d.count, detail: d.holiday || undefined }))}
        format={count}
        emptyLabel={t('dashboard.noData')}
        animate
        compact
      />
    ),
    hoursByWorkType: (
      <DonutChart
        title={t('homewall.hoursByWorkType')}
        subtitle={t('dashboard.thisMonthCompany')}
        buckets={data?.hours_by_work_type ?? []}
        format={hours}
        centerLabel={t('homewall.hoursShort')}
        emptyLabel={t('dashboard.noData')}
        animate
        compact
      />
    ),
    productCoverage: (
      <TreemapChart
        title={t('dashboard.productCoverage')}
        hint={t('dashboard.productCoverageHint')}
        otherLabel={t('dashboard.chartOther')}
        buckets={data?.org.by_product ?? []}
        format={count}
        emptyLabel={t('dashboard.noData')}
        animate
      />
    ),
    desksByWeekday: (
      <AreaChart
        title={t('homewall.desksByWeekday')}
        hint={t('homewall.desksByWeekdayHint')}
        points={weekdays.map((b) => ({ label: b.label, value: b.value }))}
        format={hours}
        emptyLabel={t('dashboard.noData')}
        caption={
          busy
            ? t('homewall.weekdayCaption', { peak: weekdays[busy.peak]!.label, trough: weekdays[busy.trough]!.label })
            : undefined
        }
        animate
      />
    ),
    topDesks: (
      <DotPlot
        title={t('homewall.topDesks')}
        subtitle={t('homewall.last90')}
        buckets={data?.desks.top_desks ?? []}
        format={count}
        emptyLabel={t('dashboard.noData')}
      />
    ),
  }
}

/** The company charts as one auto-sliding strip, in the order `keys` gives. */
export function CompanyChartCarousel({ data, keys }: { data: Company | undefined; keys: readonly OrgChartKey[] }) {
  const { t } = useTranslation()
  const charts = useCompanyCharts(data)
  return (
    <AutoSlide
      label={t('homewall.chartsCarousel')}
      slides={keys.map((key) => chartSlide(key, charts[key], CHART_WEIGHT[key], chartGroupOf(key)))}
    />
  )
}
